/**
 * Procedural theatre generator (see docs/Map-Generation.md).
 *
 * Pipeline — each stage draws from its own seeded stream, so same params ⇒ byte-identical GameMap:
 *   landmass → elevation → lakes → moisture → rivers → terrain → capitals → territories →
 *   cities → names → roads & rails → regions → theatre name
 */
import { CAPITAL_VP_BONUS, CITY_YIELDS } from '../core/constants';
import { type City, type GameMap, MAX_FACTIONS, NO_FACTION, Terrain } from '../core/types';
import { cityTarget, isCoastal, placeCapitals, placeCities, recentreCapitals, siteScores } from './cities';
import { createContext, landHexes, type MapGenParams, stageRng } from './context';
import { raiseElevation } from './elevation';
import { placeLakes } from './lakes';
import { shapeLandmass } from './landmass';
import { type FactionIdentity, NameBook, planFactions, theatreName } from './names';
import { carveRegions } from './regions';
import { carveRivers } from './rivers';
import { buildRoads } from './roads';
import { addRiverMoisture, classifyTerrain, computeMoisture } from './terrain';
import { growTerritories, TOLERANCE } from './territories';

export type { MapGenParams } from './context';
export type { FactionIdentity } from './names';

/** Names and adjectives of the factions of a match; identical to what generateMap uses. */
export function generateFactionIdentities(seed: number, count: number): FactionIdentity[] {
  return planFactions(seed >>> 0, count).map(({ name, adjective }) => ({ name, adjective }));
}

export function generateMap(params: MapGenParams): GameMap {
  const { width, height, factionCount } = params;
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 24 || height < 18) {
    throw new RangeError(`generateMap: board ${width}×${height} is too small (min 24×18)`);
  }
  if (!Number.isInteger(factionCount) || factionCount < 1 || factionCount > MAX_FACTIONS) {
    throw new RangeError(`generateMap: factionCount must be 1..${MAX_FACTIONS}, got ${factionCount}`);
  }
  const ctx = createContext(params);
  const { seed, n, terrain } = ctx;

  // Physical geography.
  shapeLandmass(ctx, stageRng(seed, 'landmass'));
  raiseElevation(ctx, stageRng(seed, 'elevation'));
  placeLakes(ctx, stageRng(seed, 'lakes'));
  computeMoisture(ctx, stageRng(seed, 'moisture'));
  const rivers = carveRivers(ctx, stageRng(seed, 'rivers'));
  addRiverMoisture(ctx);
  classifyTerrain(ctx, stageRng(seed, 'terrain'));

  // Political geography.
  const cityRng = stageRng(seed, 'cities');
  const score = siteScores(ctx, rivers);
  let capitals = placeCapitals(ctx, cityRng, score, factionCount);
  let territory = growTerritories(ctx, capitals);
  // Balanced Lloyd: while territories stay lopsided, re-centre capitals in their land and regrow.
  for (let round = 0; round < 4 && territory.ratio > TOLERANCE; round++) {
    const moved = recentreCapitals(ctx, cityRng, score, territory.owner, capitals);
    const next = growTerritories(ctx, moved);
    if (next.ratio >= territory.ratio) break;
    capitals = moved;
    territory = next;
  }
  const owner = territory.owner;
  const sites = placeCities(ctx, cityRng, score, owner, capitals, cityTarget(n, factionCount));

  const plans = planFactions(seed, factionCount);
  const names = new NameBook(stageRng(seed, 'names'));
  for (const p of plans) names.reserve(p.land);
  const cityAt = new Int16Array(n).fill(-1);
  const cities: City[] = sites.map((s, id) => {
    const coastal = isCoastal(ctx, s.hex);
    const yields = CITY_YIELDS[s.size];
    cityAt[s.hex] = id;
    return {
      id,
      hex: s.hex,
      name: names.city(plans[s.owner].style, { coastal, river: rivers.bankWidth[s.hex] > 0 }),
      size: s.size,
      capitalOf: s.capital ? s.owner : NO_FACTION,
      vp: yields.vp + (s.capital ? CAPITAL_VP_BONUS : 0),
      industry: yields.industry,
      manpower: yields.manpower,
      coastal,
    };
  });
  for (const c of cities) terrain[c.hex] = Terrain.Urban;

  const { roads, rails } = buildRoads(ctx, cities);
  const { region, regions } = carveRegions(ctx, owner, cities, plans, names);

  const land = landHexes(ctx);
  const rough = land.filter((h) => terrain[h] === Terrain.Hills || terrain[h] === Terrain.Mountains).length;
  const name = theatreName(
    stageRng(seed, 'theatre'),
    plans,
    regions.map((r) => r.name),
    rough / land.length > 0.2,
  );

  const elevation = new Uint8Array(n);
  const moisture = new Uint8Array(n);
  for (let h = 0; h < n; h++) {
    elevation[h] = Math.round(Math.min(255, Math.max(0, ctx.elevation[h])));
    moisture[h] = Math.round(Math.min(1, Math.max(0, ctx.moisture[h])) * 255);
  }
  return {
    seed: params.seed,
    name,
    width,
    height,
    terrain,
    elevation,
    moisture,
    riverEdges: ctx.riverEdges,
    rivers: rivers.paths,
    roads,
    rails,
    cities,
    cityAt,
    initialOwner: owner,
    region,
    regions,
    factionCount,
  };
}
